import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, Droplets } from "lucide-react";
import axios from "axios";
import { SEO, buildProductJsonLd } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";
import { HeroImage } from "@/components/HeroImage";
import { EnquiryForm } from "@/components/EnquiryForm";
import { BuyNowButton } from "@/components/BuyNowButton";
import { AddToCartButton } from "@/components/AddToCartButton";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { RecentlyViewedStrip } from "@/components/RecentlyViewedStrip";
import { ProductGallery, normalizeGalleryImage } from "@/components/ProductGallery";
import { WhyItsDifferentSection } from "@/components/WhyItsDifferentSection";
import { VideoLightbox } from "@/components/VideoLightbox";

const API = process.env.REACT_APP_BACKEND_URL;
const SEASONING_TEXT =
  "Canvas tents with cotton thread should be seasoned prior to use to attain optimal weatherproofness. Set the tent up on a hot sunny day, drench it thoroughly by hose — paying extra attention to sewn seams — and let it dry in the sun. Repeat 3–4 times. This naturally swells and tightens the cotton threads, sealing needle holes long-term (better than seam tape, which peels off over time). Following seasoning, any needle holes that haven't sealed naturally can be sealed with wax or another seam sealant product — then the tent is ready for heavy-duty service.";
const fmt = (n) => n.toLocaleString("en-NZ");

export default function GroundTentDetailPage() {
  const { slug } = useParams();
  const [models, setModels] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/groundtent`).then((res) => setModels(res.data)).catch((err) => { console.error(err); setModels([]); });
  }, []);

  const model = models?.find((m) => m.slug === slug);
  // No size pre-selected — Junior vs Senior carry different prices, so the
  // shopper must actively choose one before an exact price / Buy Now is shown
  // (mirrors the mandatory "Select" dropdown on the old Wix product pages).
  const [activeSize, setActiveSize] = useState(null);
  const recentlyViewed = useRecentlyViewed(
    model && { path: `/ground-tents/${model.slug}`, name: model.name, thumb: model.thumb, price: model.price, priceFrom: model.priceFrom }
  );

  useEffect(() => {
    setActiveSize(null);
  }, [slug]);

  if (models === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }
  if (!model) return <Navigate to="/ground-tents" replace />;

  const related = models
    .filter((m) => m.type === model.type && m.slug !== model.slug)
    .slice(0, 3);

  const hasSizes = model.sizes && model.sizes.length > 0;
  // Each size's price = base `price` + its own `priceDelta` (0 for the
  // cheapest size, which should match the base `price` field exactly).
  const sizePriceAt = (i) => model.price + (model.sizes[i].priceDelta || 0);
  const inStockSizeIdx = hasSizes ? model.sizes.map((_, i) => i).filter((i) => model.sizes[i].inStock !== false) : [];
  const lowestSizePrice = hasSizes
    ? Math.min(...(inStockSizeIdx.length > 0 ? inStockSizeIdx : model.sizes.map((_, i) => i)).map(sizePriceAt))
    : model.price;
  const displayPrice = hasSizes && activeSize !== null ? sizePriceAt(activeSize) : lowestSizePrice;
  const showFromPrefix = model.priceFrom && !(hasSizes && activeSize !== null);

  const gallery = model.gallery && model.gallery.length > 0
    ? model.gallery.map(normalizeGalleryImage)
    : [{ src: model.thumb, alt: null, type: "image", pos: "center" }];
  const specs = hasSizes ? model.sizes[activeSize ?? 0].specs : model.specs;
  const categoryLabel = model.type === "bow-tent" ? "BOW TENT" : model.type === "safari-tent" ? "SAFARI TENT" : "ACCESSORY";
  const sizeSelectionMissing = hasSizes && activeSize === null;
  const canBuyNow = !model.priceOnRequest && model.inStock !== false && (!hasSizes || inStockSizeIdx.length > 0);

  return (
    <div data-testid="ground-tent-detail-page">
      <SEO
        path={`/ground-tents/${model.slug}`}
        title={`${model.name} · NZ`}
        description={`${model.name} — ${model.tagline}. ${model.intro.slice(0, 140)}`}
        image={model.hero || model.thumb}
        jsonLd={buildProductJsonLd({ name: model.name, description: model.tagline, image: model.hero || model.thumb, price: model.price, priceOnRequest: model.priceOnRequest, inStock: model.inStock, url: `/ground-tents/${model.slug}` })}
      />

      {/* HERO */}
      <section className="relative min-h-[70svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage src={model.hero || model.thumb} alt={model.name} className="w-full h-full" desktopPosition={model.heroPosition || "50% center"} mobilePosition={model.heroFocalMobile} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-8% via-black/40 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-16 pt-32 w-full">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <Link to="/ground-tents" data-testid="detail-breadcrumb" className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-[var(--brand-primary)] transition-colors">
              ← Ground Tents
            </Link>
            {model.badge && (
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
                {model.badge}
              </span>
            )}
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/80 bg-black/60 backdrop-blur-sm px-2.5 py-1">
              {categoryLabel}
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-white hero-text-shadow max-w-4xl">
            {model.name.toUpperCase()}
          </h1>
          <p className="text-base md:text-lg text-[var(--brand-primary)] font-mono uppercase tracking-widest mt-5">
            {model.tagline}
          </p>
          <div className="mt-8 flex items-start gap-6 flex-wrap">
            <div>
              <p className="font-display text-4xl md:text-5xl text-white leading-none">
                {showFromPrefix && <span className="text-lg font-mono uppercase tracking-widest text-white/70 mr-2">FROM</span>}
                ${fmt(displayPrice)}
              </p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mt-2">
                GST included · {model.availability}
              </p>
            </div>
            <div className="flex items-start gap-3 flex-wrap">
              {canBuyNow && (
                <>
                  <BuyNowButton
                    productPath={`/ground-tents/${model.slug}`}
                    testId="detail-cta-buy-now"
                    selectedOptions={hasSizes && activeSize !== null ? { size: model.sizes[activeSize].label } : null}
                    disabled={sizeSelectionMissing}
                    disabledHint={sizeSelectionMissing ? "Select a size below to continue" : ""}
                  />
                  <AddToCartButton
                    productPath={`/ground-tents/${model.slug}`}
                    testId="detail-cta-add-to-cart"
                    name={model.name}
                    thumb={model.thumb}
                    unitPrice={displayPrice}
                    priceFrom={showFromPrefix}
                    selectedOptions={hasSizes && activeSize !== null ? { size: model.sizes[activeSize].label } : null}
                    disabled={sizeSelectionMissing}
                  />
                </>
              )}
              <a href="#enquire" data-testid="detail-cta-enquire" className="btn-secondary">
                Enquire About This <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
          {hasSizes && (
            <div className="mt-8 max-w-lg" data-testid="size-selector">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mb-3">
                Choose Size <span className="text-[var(--brand-primary)]">*</span>
              </p>
              <div className="flex gap-2 flex-wrap">
                {model.sizes.map((s, i) => {
                  const outOfStock = s.inStock === false;
                  return (
                    <button
                      key={s.label}
                      onClick={() => !outOfStock && setActiveSize(i)}
                      disabled={outOfStock}
                      data-testid={`size-option-${s.label.toLowerCase()}`}
                      className={`px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] border transition-colors ${
                        outOfStock
                          ? "border-white/10 text-white/30 cursor-not-allowed line-through"
                          : i === activeSize
                          ? "bg-[var(--brand-primary)] text-black border-[var(--brand-primary)]"
                          : "border-white/30 text-white/70 hover:border-white/60"
                      }`}
                    >
                      {s.label} <span className="opacity-70">${fmt(sizePriceAt(i))}</span>
                      {outOfStock && <span className="ml-1.5 not-italic">— Sold Out</span>}
                    </button>
                  );
                })}
              </div>
              {sizeSelectionMissing && (
                <p data-testid="size-selector-hint" className="text-xs text-white/50 mt-3">
                  Select a size to see the exact price and enable Buy Now.
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* GALLERY + INTRO */}
      <section className="container-x py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <ProductGallery gallery={gallery} productName={model.name} />
          </div>

          {/* Intro + Features */}
          <div className="lg:col-span-5 space-y-8">
            <div>
              <p className="overline mb-4">— About This Tent</p>
              <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed whitespace-pre-line">{model.intro}</p>
            </div>
            <div className="border-t border-[var(--border-default)] pt-8">
              <p className="overline mb-5">— Key Features</p>
              <ul className="space-y-3">
                {model.features.map((f, i) => (
                  <li key={f || i} className="flex items-start gap-3 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                    <Check size={16} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY IT'S DIFFERENT */}
      <WhyItsDifferentSection contentSections={model.contentSections} />

      {/* SPECS + SIZE SELECTOR */}
      <section className="border-y border-[var(--border-default)] bg-[var(--bg-secondary)]">
        <div className="container-x py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="overline mb-4">— Specifications</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                THE <span className="text-[var(--brand-primary)]">NUMBERS.</span>
              </h2>
              {hasSizes && (
                <p className="text-sm md:text-base text-[var(--text-secondary)] mt-4">
                  Showing specs for <span className="text-[var(--brand-primary)]">{model.sizes[activeSize ?? 0].label}</span> — change your size selection above the gallery to compare.
                </p>
              )}
            </div>
            <div className="lg:col-span-8">
              <dl className="divide-y divide-[var(--border-default)] border-t border-b border-[var(--border-default)]">
                {specs.map(([k, v], i) => (
                  <div key={k || i} data-testid={`spec-row-${i}`} className="grid grid-cols-3 gap-4 py-4">
                    <dt className="font-mono text-xs uppercase tracking-wider text-[var(--brand-primary)] col-span-1">{k}</dt>
                    <dd className="text-base md:text-lg text-white col-span-2">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* PACKDOWN VIDEO — Wanderer and Safari Deluxe only */}
      {["wanderer", "safari-deluxe"].includes(model.slug) && (
        <section className="container-x py-16 md:py-20" data-testid="packdown-video-section">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="overline mb-4">— Worth Watching</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              HOW TO <span className="text-[var(--brand-primary)]">PACK IT DOWN.</span>
            </h2>
          </div>
          <div className="max-w-4xl mx-auto relative aspect-video overflow-hidden border border-[var(--border-default)] bg-black">
            <VideoLightbox
              videoId="qiojA8K9cmw"
              title={`${model.name} — packdown, demonstrated`}
              testId="packdown-video"
              caption="Watch The Packdown"
            />
          </div>
        </section>
      )}

      {/* SEASONING NOTE */}
      {model.seasoningRequired && (
        <section className="container-x py-16 md:py-20" data-testid="seasoning-note">
          <div className="border border-[var(--brand-primary)]/30 bg-[var(--bg-secondary)] p-6 md:p-10">
            <div className="flex items-start gap-4 md:gap-6">
              <Droplets size={28} className="text-[var(--brand-primary)] shrink-0 mt-1" strokeWidth={1.6} />
              <div>
                <p className="overline mb-3">— Before Your First Trip</p>
                <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-4">
                  SEASONING REQUIRED.
                </h3>
                <p className="text-[var(--text-secondary)] leading-relaxed max-w-3xl">{SEASONING_TEXT}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* RELATED */}
      {related.length > 0 && (
        <section className="container-x py-16 md:py-20">
          <div className="mb-8">
            <p className="overline mb-3">— You Might Also Consider</p>
            <h2 className="font-display text-3xl md:text-4xl text-white">
              MORE <span className="text-[var(--brand-primary)]">TENTCO GEAR</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((r, i) => (
              <Link
                key={r.slug}
                to={`/ground-tents/${r.slug}`}
                data-testid={`related-tent-${i}`}
                className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <HoverImage primary={r.thumb} secondary={r.gallery?.[1]?.src} alt={r.name} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-display text-xl md:text-2xl text-white leading-tight">{r.name}</h3>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--brand-primary)] mt-2">
                      {r.priceFrom ? "FROM " : ""}${fmt(r.price)}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <RecentlyViewedStrip items={recentlyViewed} />

      {/* ENQUIRY */}
      <section id="enquire" className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)] scroll-mt-24">
        <div className="container-x py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="overline mb-4">— Enquire About The {model.name.split(" ").slice(0, 3).join(" ")}</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                ASK US<br />
                <span className="text-[var(--brand-primary)]">ANYTHING.</span>
              </h2>
              <p className="text-[var(--text-secondary)] mt-6 leading-relaxed">
                Sizes, lead times, netted ground sheets and extensions — get a proper answer from someone who lives this stuff.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-[var(--border-default)] bg-[var(--bg-main)] p-6 md:p-8">
                <EnquiryForm defaultProduct={`Ground Tent — ${model.name}`} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
